import { useLazyQuery } from '@apollo/client'
import { isEmpty } from 'lodash'
import { useState } from 'react'
import { useIntl, defineMessages } from 'react-intl'

import { SearchCheckboxesV2 } from 'components/SearchCheckboxesV2'
import { useDebouncedQuery } from 'components/v2/Autocomplete'
import { GET_USERS_SUGGESTIONS } from 'features/Users/queries-legacy'
import { GTUserSuggestion, GTUserSuggestionsResponse } from 'gql/types.generated'

const messages = defineMessages({
  allCashiers: {
    id: 'cashiers.checkboxes.all',
    defaultMessage: 'All Employees'
  },
  error: {
    id: 'cashiers.checkboxes.error',
    defaultMessage: 'Error loading your employees'
  },
  actionOnError: {
    id: 'cashiers.checkboxes.actionOnError',
    defaultMessage: 'Try again'
  },
  empty: {
    id: 'cashiers.checkboxes.empty',
    defaultMessage: 'No employees found'
  },
  allOption: {
    id: 'cashiers.checkboxes.all',
    defaultMessage: 'All Employees'
  },
  searchPlaceholder: {
    id: 'cashiers.checkboxes.searchPlaceholder',
    defaultMessage: 'Search employee'
  }
})

const getItemLabel = ({ firstName, lastName, username }: GTUserSuggestion): string => {
  return `${firstName} ${lastName} (${username})`
}

interface IMultiCashierCheckboxesProps {
  values: GTUserSuggestion[]
  siteIds?: string[]
  onChange: (cashiers: GTUserSuggestion[]) => void
}

export const MultiCashierCheckboxes = ({
  values,
  siteIds = [],
  onChange,
  ...props
}: IMultiCashierCheckboxesProps): JSX.Element => {
  const intl = useIntl()
  const [pattern, setPattern] = useState('')
  const [getCashiers, { data, loading, error }] = useLazyQuery<{ userSuggestions: GTUserSuggestionsResponse }>(
    GET_USERS_SUGGESTIONS
  )
  const { isDebouncing } = useDebouncedQuery({
    query: getCashiers,
    pattern,
    extraInputs: {
      type: 'cashier',
      siteIDs: !isEmpty(siteIds) ? siteIds : undefined
    }
  })

  const getInputValue = (): string => {
    if (!isEmpty(values)) {
      return values
        .map((cashier): string => {
          return `${cashier.firstName} ${cashier.lastName}`
        })
        .join(', ')
    }

    return intl.formatMessage(messages.allCashiers)
  }

  const shownItems = data?.userSuggestions?.items || []

  const handleChange = (users: GTUserSuggestion[], pattern = ''): void => {
    setPattern(pattern)

    onChange(users)
  }

  return (
    <SearchCheckboxesV2
      items={shownItems}
      loading={loading || isDebouncing}
      error={error && intl.formatMessage(messages.error)}
      emptyMessage={intl.formatMessage(messages.empty)}
      values={values}
      inputValue={getInputValue()}
      getItemLabel={getItemLabel}
      getTagLabel={(user: GTUserSuggestion): string => user.username}
      allOption={intl.formatMessage(messages.allOption)}
      searchPlaceholder={intl.formatMessage(messages.searchPlaceholder)}
      {...props}
      onChange={handleChange}
    />
  )
}
